// Exchange types
export interface ExchangeResponse {
  token?: string;
  tenant_id?: string;
  expires_in?: number;
  error?: string;
}

export interface ExchangeResult {
  token: string;
  tenant_id: string;
  expires_in: number;
}

export interface ExchangeError {
  error: string;
}

// Hint SDK types
export interface HintSDKUser {
  id: string;
  name: string;
  email: string;
  first_name: string;
  last_name: string;
  partner_roles: string[];
}

export interface HintSDKPatient {
  id: string;
  name: string;
}

export interface HintSDKInstance {
  init: (callback?: () => void) => void;
  user?: HintSDKUser;
  currentPatient?: HintSDKPatient | null;
  onCurrentPatientChanged?: (callback: () => void) => void;
  close?: () => void;
}

export type ThemeStyles = {
  background?: string;
  foreground?: string;
  card?: string;
  'card-foreground'?: string;
  popover?: string;
  'popover-foreground'?: string;
  primary?: string;
  'primary-foreground'?: string;
  secondary?: string;
  'secondary-foreground'?: string;
  muted?: string;
  'muted-foreground'?: string;
  accent?: string;
  'accent-foreground'?: string;
  destructive?: string;
  'destructive-foreground'?: string;
  border?: string;
  input?: string;
  ring?: string;
  'chart-1'?: string;
  'chart-2'?: string;
  'chart-3'?: string;
  'chart-4'?: string;
  'chart-5'?: string;
  radius?: string;
  'font-body'?: string;
  'font-heading'?: string;
};

type ThemeConfig = {
  styles?: ThemeStyles;
  darkMode?: boolean;
};

interface EngageTypedOverrides {
  user?: string;
  user_other?: string;
  automation?: string;
  automation_other?: string;
  segment?: string;
  segment_other?: string;
  broadcast?: string;
  broadcast_other?: string;
  trigger_based?: string;
  insight?: string;
  insight_other?: string;
  merge_field?: string;
  merge_field_other?: string;
  extra_merge_field_name?: string;
  extra_merge_field_name_other?: string;
  advanced_merge_field_name?: string;
  advanced_merge_field_name_other?: string;
}
interface MeasureTypedOverrides {
  transaction_table_id_header?: string;
  transaction_table_created_header?: string;
}
interface LanguageConfig {
  default: string;

  overrides?: {
    [ReachFeature.ENGAGE]: {
      en?: EngageTypedOverrides;
      es?: EngageTypedOverrides;
    };
    [ReachFeature.MEASURE]: {
      en?: MeasureTypedOverrides;
      es?: MeasureTypedOverrides;
    };
  };
}

export enum ReachFeature {
  ENGAGE = 'engage',
  MEASURE = 'measure',
  REPUTATION = 'reputation',
  ACQUIRE = 'acquire',
  VOICE = 'voice',
}

interface SDKCallbacks {
  /** Called when reauthentication is required */
  onReauthRequested: () => Promise<string>;
  // /** Called when tenant information is requested */
  onTenantInformationRequested?: () => void;
}
type FeatureKey =
  | ReachFeature.MEASURE
  | `${ReachFeature.MEASURE}-setup`
  // @deprecated -- use acquire instead
  | `${ReachFeature.ACQUIRE}-setup`
  | ReachFeature.ACQUIRE
  | `${ReachFeature.REPUTATION}-setup`
  | ReachFeature.REPUTATION
  | `${ReachFeature.ENGAGE}-segment-builder`
  | `${ReachFeature.ENGAGE}-automations-create-modal`
  | `${ReachFeature.ENGAGE}-automations-view-modal`
  | ReachFeature.ENGAGE
  | ReachFeature.VOICE;

// Reach SDK types
export interface ReachSDKConfig {
  authToken: string;
  theme?: ThemeConfig;
  /** Event callbacks */
  callbacks: SDKCallbacks;
  /** Initial feature to load */
  feature?: FeatureKey;
  /** Debug configuration */
  debug?: boolean;
  language?: LanguageConfig;
  sandbox?: boolean;
  /**  children?: React.ReactNode; */

  /**
   * Features to hide
   */
  hideFeature?: {
    [ReachFeature.ENGAGE]: {
      sms?: boolean;
    };
  };

  /**
   * Features to engage
   */
  enableFeature?: {
    [ReachFeature.ENGAGE]: {
      onboarding?: boolean;
    };
  };
}

interface ReachSDKInstance {
  dispose?: () => void;
}

declare global {
  interface Window {
    HintSDK?: HintSDKInstance;
    ReachSDK?: new (config: ReachSDKConfig) => ReachSDKInstance;
  }
}
